import { SplashScreen } from '@capacitor/splash-screen';
import Cookies from 'js-cookie';
import React, { Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';

import Loader from './components/Loader/Loader';
import Account from './pages/Account';
import Billing from './pages/Billing';
import Create from './pages/Create';
import Form from './pages/Form';
import Home from './pages/Home';
import Login from './pages/Login';
import NotFound from './pages/NotFound/NotFound';

import { getAnalytics } from './store/actions/analyticsActions';
import { loadMe, loginUserWithOauth } from './store/actions/authActions';
import { getForms, getGoogleForms } from './store/actions/formActions';

import { message } from 'antd';
import 'nprogress/nprogress.css';
import './App.less';
import { i18n } from './i18n';
import { getPlatform } from './utils/helpers';
import { getStoredAuthToken, TOKEN_KEY } from './utils/token';

import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { PushNotifications } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';
import { App as CapacitorApp } from '@capacitor/app';
import { StatusBar } from '@capacitor/status-bar';

import { NetworkStatusOverlay } from './layout/Layout';
import { updatePushToken } from './store/actions/userActions';
import useNetworkStatus from './hooks/useNetworkStatus';

const platform = getPlatform();

if (platform !== 'web') {
  StatusBar.setBackgroundColor({ color: '#fff' });
}

CapacitorApp.addListener('backButton', (event) => {
  window.history.back();
});

const App = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const auth = useSelector((state) => state.auth);

  useEffect(() => {
    const initAuth = async () => {
      let jwtToken, language;
      if (platform === 'web') {
        if (window.location.hash === '#_=_') window.location.hash = '';
        const error = Cookies.get('error');
        if (error) {
          Cookies.remove('error');
          message.error(error, 5);
        }
        jwtToken = Cookies.get(TOKEN_KEY);
        language = Cookies.get('wf-language');
        if (language) Cookies.remove('wf-language', { path: '/', domain: '.whatsform.com' });
      } else {
        jwtToken = await getStoredAuthToken();
      }

      if (jwtToken) {
        dispatch(loginUserWithOauth(jwtToken, language));
      } else {
        dispatch(loadMe(language));
      }
    };
    initAuth();
  }, [dispatch, history]);

  // initalise forms
  useEffect(() => {
    if (auth.isAuthenticated) {
      dispatch(getForms());
      dispatch(getGoogleForms());
      dispatch(getAnalytics());
      // if templateId was set from website, route to create page
      const templateId = Cookies.get('templateId');
      if (templateId && window.location.pathname === '/') {
        history.push('/build');
      }
    }
  }, [auth.isAuthenticated, dispatch, history]);

  useEffect(() => {
    // affiliate tracking
    if (Cookies.get('wf-new') && auth.me?.email && window.fpr) {
      Cookies.remove('wf-new');
      window.fpr('referral', { email: auth.me.email });
    }
  }, [auth.me]);

  const PrivateRoute = ({ component, ...options }) => {
    if (auth.isAuthenticated) {
      return <Route {...options} component={component} />;
    } else {
      return <Redirect to="/login" />;
    }
  };

  /**
   * Push Notifications Setup
   *
   * Handles device registration for push notifications on app startup:
   * 1. Checks if push notification permission is granted
   *    - If granted: Registers device and updates token in backend
   *    - If not granted: Checks if user previously denied notifications
   *      - If not denied: Requests permission
   *      - If denied: Stores preference to avoid future prompts
   *
   * Note: Registration is called on every app start to ensure the backend
   * always has the latest token, as tokens can change when:
   * - The app is reinstalled
   * - The user clears app data
   * - The token expires
   * - The device settings are reset
   */
  useEffect(() => {
    if (platform === 'web' || !auth.isAuthenticated) return;
    const registerPushNotifications = () => {
      PushNotifications.register();
      PushNotifications.addListener('registration', (token) => {
        if (auth.me?.deviceTokens?.includes(token.value)) return;
        dispatch(updatePushToken(token.value));
      });
      PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        const formId = notification.notification?.data?.formId;
        if (formId) {
          history.push(`/forms/${formId}/responses`);
        }
      });
    };

    PushNotifications.checkPermissions().then((res) => {
      if (res.receive === 'granted') {
        registerPushNotifications();
      } else {
        PushNotifications.requestPermissions().then((res) => {
          if (res.receive === 'granted') {
            registerPushNotifications();
          } else {
            Preferences.set({ key: 'pushNotificationsEnabled', value: 'false' });
          }
        });
      }
    });
  }, [auth, dispatch]);

  return (
    <>
      {auth.appLoaded ? (
        <Switch>
          <Route path="/notfound" component={NotFound} />
          <Route path="/login" component={Login} />
          <PrivateRoute exact path="/" component={Home} />
          <PrivateRoute exact path="/build" component={Create} />
          <PrivateRoute exact path="/billing" component={Billing} />
          <PrivateRoute exact path="/account" component={Account} />
          <PrivateRoute path="/forms/:id/:section" component={Form} />
          <PrivateRoute path="/forms/:id" component={Form} />
          <Route component={NotFound} />
        </Switch>
      ) : null}
    </>
  );
};

export default function WrappedApp() {
  const isOnline = useNetworkStatus();
  const auth = useSelector((state) => state.auth);
  const language = useSelector((state) => state.user.language);

  const history = useHistory();

  useEffect(() => {
    i18n.changeLanguage(language ?? 'en').catch((error) => {
      console.error('Error changing language:', error);
    });
  }, [language]);

  useEffect(() => {
    CapacitorUpdater.notifyAppReady();
  }, []);

  useEffect(() => {
    if (auth.appLoaded || !isOnline) {
      SplashScreen.hide();
    }
  }, [auth.appLoaded, isOnline]);

  return <Suspense fallback={<Loader />}>{isOnline ? <App /> : <NetworkStatusOverlay />}</Suspense>;
}
